import React, { useMemo, useState } from 'react';
import { TEAM_COLORS, teamColor } from './BattleResults';

const GENRES = ['Rap', 'Hip-Hop', 'Drill', 'R&B', 'Pop', 'EDM', 'Country', 'Latin Pop', 'Reggaeton', 'Latin', 'Cumbia', 'Salsa', 'Reggae', 'Rock', 'Alternative R&B', 'K-Pop', 'Trap', 'Classical', 'Other'];
const emptyPractice = (team = 't1') => ({ team_key: team, kind: 'practice', title: '', artist: '', genre: '', youtube_url: '', selector_name: '' });

export function initialBattleDraft() {
  return {
    mode: 'practice', name: '',
    teams: [
      { key: 't1', name: 'Purple Team', color: 'purple' },
      { key: 't2', name: 'Blue Team', color: 'blue' },
    ],
    settings: { tiebreak_wildcard: true },
    judges: ['', '', ''], entries: [emptyPractice('t1'), emptyPractice('t2')],
  };
}

function seatLabel(seat, namesEnabled) {
  return namesEnabled && seat.display_name ? seat.display_name : `Seat ${seat.seat_number}`;
}

// Team entries name a kid by seat number in this class; seat ids never reach the browser.
function seatKey(seat) {
  return `seat-${seat.seat_number}`;
}

function teamEntry(seat, teamKey) {
  const beats = seat.beats || [];
  const preferred = beats.find((beat) => beat.starred) || beats[0];
  return preferred ? { team_key: teamKey, kind: 'original', seat_number: seat.seat_number, beat_slot: preferred.slot, _seat_key: seatKey(seat) } : null;
}

function validate(draft) {
  const errors = [];
  if (draft.entries.length > 40) errors.push('Battles can have at most 40 entries.');
  if (!draft.name.trim()) errors.push('Give this battle a name.');
  if (draft.teams.some((team) => !team.name.trim())) errors.push('Every team needs a name.');
  const judges = draft.judges.filter((name) => name.trim());
  if (judges.length < 3 || judges.length > 5) errors.push('Add 3 to 5 judge names.');
  if (draft.entries.length < draft.teams.length) errors.push('Every team needs at least one entry.');
  if (draft.teams.some((team) => !draft.entries.some((entry) => entry.team_key === team.key))) errors.push('Every team needs at least one entry.');
  if (draft.mode === 'practice') {
    if (draft.entries.some((entry) => !entry.title.trim() || !entry.artist.trim())) errors.push('Every practice entry needs a title and artist.');
    if (draft.entries.some((entry) => entry.genre === 'Other' && !entry.other_genre?.trim())) errors.push('Add the genre name for every Other selection.');
    if (draft.entries.some((entry) => entry.youtube_url && !/^https:\/\/(www\.|m\.)?youtube\.com\//i.test(entry.youtube_url) && !/^https:\/\/youtu\.be\//i.test(entry.youtube_url))) errors.push('YouTube links must use an approved HTTPS YouTube address.');
  } else {
    if (!draft.entries.length) errors.push('Pick at least one kid.');
  }
  return [...new Set(errors)];
}

export function BattleBuilder({ seats = [], namesEnabled = false, onSave, onCancel, busy = false }) {
  const [draft, setDraft] = useState(initialBattleDraft);
  const [errors, setErrors] = useState([]);
  const selected = useMemo(() => new Map(draft.entries.map((entry) => [entry._seat_key || `seat-${entry.seat_number}`, entry])), [draft.entries]);
  const patch = (value) => setDraft((current) => ({ ...current, ...value }));
  const patchTeam = (index, value) => patch({ teams: draft.teams.map((team, item) => item === index ? { ...team, ...value } : team) });
  const patchEntry = (index, value) => patch({ entries: draft.entries.map((entry, item) => item === index ? { ...entry, ...value } : entry) });
  const setMode = (mode) => patch({ mode, entries: mode === 'practice'
    ? [emptyPractice(draft.teams[0].key), emptyPractice(draft.teams[1].key)] : [] });

  const toggleSeat = (seat) => {
    const id = seatKey(seat);
    if (selected.has(id)) patch({ entries: draft.entries.filter((entry) => (entry._seat_key || `seat-${entry.seat_number}`) !== id) });
    else {
      const entry = teamEntry(seat, draft.teams[draft.entries.length % draft.teams.length].key);
      if (entry) patch({ entries: [...draft.entries, entry] });
    }
  };
  const autoBalance = () => {
    const entries = draft.entries.length ? draft.entries : seats.map((seat, index) => teamEntry(seat, draft.teams[index % draft.teams.length].key)).filter(Boolean);
    patch({ entries: entries.map((entry, index) => ({ ...entry, team_key: draft.teams[index % draft.teams.length].key })) });
  };
  const submit = async (start) => {
    const nextErrors = validate(draft);
    setErrors(nextErrors);
    if (nextErrors.length) return;
    const payload = {
      ...draft,
      name: draft.name.trim(),
      teams: draft.teams.map((team) => ({ ...team, name: team.name.trim() })),
      judges: draft.judges.map((name) => name.trim()).filter(Boolean),
      entries: draft.entries.map((entry) => {
        if (draft.mode !== 'practice') {
          const item = { ...entry };
          delete item._seat_key;
          return item;
        }
        const { other_genre: otherGenre, ...item } = entry;
        return {
          ...item,
          title: entry.title.trim(), artist: entry.artist.trim(),
          genre: (entry.genre === 'Other' ? otherGenre?.trim() : entry.genre.trim()) || null,
          youtube_url: entry.youtube_url.trim() || null, selector_name: entry.selector_name.trim() || null,
        };
      }),
    };
    await onSave(payload, start);
  };

  return <section className="battle-builder" aria-labelledby="new-battle-title">
    <div className="battle-section-heading"><div><span>Battle setup</span><h3 id="new-battle-title">New battle</h3></div><button onClick={onCancel}>Cancel</button></div>
    {errors.length > 0 && <div className="battle-form-errors" role="alert">{errors.map((error) => <p key={error}>{error}</p>)}</div>}
    <div className="battle-form-grid">
      <label>Mode<select value={draft.mode} onChange={(event) => setMode(event.target.value)}><option value="practice">Practice</option><option value="team">Team battle</option></select></label>
      <label>Battle name<input value={draft.name} maxLength="100" onChange={(event) => patch({ name: event.target.value })} /></label>
    </div>
    <fieldset className="battle-teams"><legend>Teams</legend>
      {draft.teams.map((team, index) => <div key={team.key}>
        <span className="battle-team-swatch" style={{ '--team-color': teamColor(team.color) }} />
        <label>Team {index + 1} name<input aria-label={`Team ${index + 1} name`} value={team.name} maxLength="40" onChange={(event) => patchTeam(index, { name: event.target.value })} /></label>
        <label>Color<select aria-label={`Team ${index + 1} color`} value={team.color} onChange={(event) => patchTeam(index, { color: event.target.value })}>{TEAM_COLORS.map((color) => <option key={color}>{color}</option>)}</select></label>
        {draft.teams.length > 2 && <button aria-label={`Remove team ${index + 1}`} onClick={() => {
          const teams = draft.teams.filter((_, item) => item !== index);
          patch({ teams, entries: draft.entries.filter((entry) => entry.team_key !== team.key) });
        }}>Remove</button>}
      </div>)}
      {draft.teams.length < 4 && <button onClick={() => {
        const index = draft.teams.length;
        patch({ teams: [...draft.teams, { key: `t${index + 1}`, name: `Team ${index + 1}`, color: TEAM_COLORS[index] }] });
      }}>Add team</button>}
    </fieldset>
    <fieldset className="battle-judges"><legend>Judges</legend>
      {draft.judges.map((judge, index) => <label key={index}>Judge {index + 1}<input value={judge} maxLength="60" onChange={(event) => patch({ judges: draft.judges.map((name, item) => item === index ? event.target.value : name) })} /></label>)}
      <div>{draft.judges.length < 5 && <button onClick={() => patch({ judges: [...draft.judges, ''] })}>Add judge</button>}{draft.judges.length > 3 && <button onClick={() => patch({ judges: draft.judges.slice(0, -1) })}>Remove judge</button>}</div>
    </fieldset>
    <label className="battle-wildcard-toggle"><input type="checkbox" checked={draft.settings.tiebreak_wildcard} onChange={(event) => patch({ settings: { tiebreak_wildcard: event.target.checked } })} />Use one wild-card pick per judge to break ties</label>
    {draft.mode === 'practice' ? <fieldset className="battle-entries"><legend>Practice entries</legend>
      {draft.entries.map((entry, index) => <article key={index}>
        <div className="battle-entry-number">#{index + 1}</div>
        <label>Team<select aria-label={`Entry ${index + 1} team`} value={entry.team_key} onChange={(event) => patchEntry(index, { team_key: event.target.value })}>{draft.teams.map((team) => <option key={team.key} value={team.key}>{team.name}</option>)}</select></label>
        <label>Title<input aria-label={`Entry ${index + 1} title`} value={entry.title} maxLength="100" onChange={(event) => patchEntry(index, { title: event.target.value })} /></label>
        <label>Artist<input aria-label={`Entry ${index + 1} artist`} value={entry.artist} maxLength="80" onChange={(event) => patchEntry(index, { artist: event.target.value })} /></label>
        <label>Genre<select aria-label={`Entry ${index + 1} genre`} value={entry.genre} onChange={(event) => patchEntry(index, { genre: event.target.value })}><option value="">Optional</option>{GENRES.map((genre) => <option key={genre}>{genre}</option>)}</select></label>
        {entry.genre === 'Other' && <label>Genre name<input aria-label={`Entry ${index + 1} genre name`} value={entry.other_genre || ''} maxLength="50" onChange={(event) => patchEntry(index, { other_genre: event.target.value })} /></label>}
        <label>YouTube link<input aria-label={`Entry ${index + 1} YouTube link`} type="url" value={entry.youtube_url} onChange={(event) => patchEntry(index, { youtube_url: event.target.value })} /></label>
        {namesEnabled && <label>Pick a kid<select aria-label={`Entry ${index + 1} kid`} value="" onChange={(event) => patchEntry(index, { selector_name: event.target.value })}><option value="">Choose a kid</option>{seats.filter((seat) => seat.display_name).map((seat) => <option key={seat.seat_number} value={seat.display_name}>{seat.display_name}</option>)}</select></label>}
        <label>Stage name<input aria-label={`Entry ${index + 1} stage name`} value={entry.selector_name} maxLength="60" onChange={(event) => patchEntry(index, { selector_name: event.target.value })} /></label>
        {draft.entries.length > 2 && <button aria-label={`Remove entry ${index + 1}`} onClick={() => patch({ entries: draft.entries.filter((_, item) => item !== index) })}>Remove</button>}
      </article>)}
      {draft.entries.length < 40 && <button onClick={() => patch({ entries: [...draft.entries, emptyPractice(draft.teams[draft.entries.length % draft.teams.length].key)] })}>Add practice entry</button>}
    </fieldset> : <fieldset className="battle-kid-picker"><legend>Pick kids and beats</legend>
      <button className="battle-auto-balance" onClick={autoBalance}>Auto-balance teams</button>
      <div>{seats.map((seat) => {
        const id = seatKey(seat); const entry = selected.get(id); const beats = seat.beats || [];
        const beat = (entry && beats.find((item) => item.slot === entry.beat_slot)) || beats.find((item) => item.starred) || beats[0];
        return <article key={seat.seat_number}>
          <label className="battle-kid-check"><input type="checkbox" checked={Boolean(entry)} disabled={!beats.length} onChange={() => toggleSeat(seat)} />{seatLabel(seat, namesEnabled)}</label>
          {entry && <>
            <label>Team<select aria-label={`${seatLabel(seat, namesEnabled)} team`} value={entry.team_key} onChange={(event) => patch({ entries: draft.entries.map((item) => (item._seat_key || `seat-${item.seat_number}`) === id ? { ...item, team_key: event.target.value } : item) })}>{draft.teams.map((team) => <option key={team.key} value={team.key}>{team.name}</option>)}</select></label>
            <label>Beat<select aria-label={`${seatLabel(seat, namesEnabled)} beat`} value={entry.beat_slot} onChange={(event) => patch({ entries: draft.entries.map((item) => (item._seat_key || `seat-${item.seat_number}`) === id ? { ...item, beat_slot: Number(event.target.value) } : item) })}>{beats.map((item) => <option key={item.slot} value={item.slot}>{item.title || `Beat ${item.slot}`}{item.starred ? ' (starred)' : ''}</option>)}</select></label>
          </>}
          {beat && beat.takehome?.status !== 'ready' && <strong className="battle-needs-takehome">needs Take it home</strong>}
          {!beats.length && <span>No beats yet</span>}
        </article>;
      })}</div>
    </fieldset>}
    <div className="battle-builder-actions"><button disabled={busy} onClick={() => submit(false)}>Save setup</button><button className="primary" disabled={busy} onClick={() => submit(true)}>{busy ? 'Saving...' : 'Start battle'}</button></div>
  </section>;
}
